import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Mic2, Trophy, Upload, User, LogOut, Menu, X, BarChart3 } from 'lucide-react';

const LOGO_URL = "/twlogo.svg";

const getTierColor = (tier) => {
    const colors = {
        rookie:    'tier-rookie',
        contender: 'tier-contender',
        prospect:  'tier-prospect',
        pro:       'tier-pro',
        elite:     'tier-elite',
        legend:    'tier-legend',
    };
    return colors[tier] || colors.rookie;
};

export const Layout = ({ children }) => {
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const navItems = [
        { path: '/feed', label: 'Feed', icon: Mic2 },
        { path: '/practice', label: 'Practice', icon: Mic2 },
        { path: '/charts', label: 'Charts', icon: BarChart3 },
        { path: '/leaderboard', label: 'Rankings', icon: Trophy },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <div className="min-h-screen bg-void" style={{backgroundImage:'radial-gradient(60% 40% at 12% 0%,rgba(0,229,255,.05),transparent 60%),radial-gradient(55% 45% at 92% 8%,rgba(255,61,203,.04),transparent 60%)'}}>
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 border-b" style={{background:'#000000',borderBottomColor:'rgba(0,229,255,.12)'}}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <Link to="/" className="flex items-center min-w-0" data-testid="logo-link">
                            <img
                                src="/twbanner.png"
                                alt="TrapWorld"
                                className="h-10 sm:h-12 w-auto object-contain flex-shrink-0"
                            />
                        </Link>

                        {/* Desktop Nav */}
                        <nav className="hidden md:flex items-center gap-1">
                            {navItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    data-testid={`nav-${item.label.toLowerCase()}`}
                                    className={`px-4 py-2 rounded-sm text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                                        isActive(item.path)
                                            ? 'bg-white/10 text-white'
                                            : 'text-zinc-400 hover:text-white hover:bg-white/5'
                                    }`}
                                >
                                    <item.icon className="w-4 h-4" />
                                    {item.label}
                                </Link>
                            ))}
                        </nav>

                        {/* Right Actions */}
                        <div className="flex items-center gap-3">
                            {isAuthenticated ? (
                                <>
                                    <Link to="/upload" data-testid="upload-btn">
                                        <Button
                                            size="sm"
                                            className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider shadow-glow-purple rounded-sm hidden sm:flex items-center gap-2"
                                        >
                                            <Upload className="w-4 h-4" />
                                            Upload
                                        </Button>
                                    </Link>

                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
                                            <button className="flex items-center gap-2 p-1 rounded-sm hover:bg-white/5 transition-colors">
                                                <Avatar className="h-8 w-8 border border-white/20">
                                                    <AvatarImage src={user?.picture} />
                                                    <AvatarFallback className="bg-paper text-xs font-mono">
                                                        {user?.username?.slice(0, 2).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="hidden sm:block text-left">
                                                    <p className="text-sm font-medium">{user?.artist_name || user?.username}</p>
                                                    <span className={`text-xs px-1.5 py-0.5 rounded-sm uppercase font-mono ${getTierColor(user?.tier)}`}>
                                                        {user?.tier}
                                                    </span>
                                                </div>
                                            </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48 bg-paper border-white/10">
                                            <DropdownMenuItem asChild>
                                                <Link to={`/profile/${user?.user_id}`} className="flex items-center gap-2" data-testid="profile-link">
                                                    <User className="w-4 h-4" />
                                                    Profile
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator className="bg-white/10" />
                                            <DropdownMenuItem
                                                onClick={handleLogout}
                                                className="text-red-400 focus:text-red-400 flex items-center gap-2"
                                                data-testid="logout-btn"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                Logout
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Link to="/login" data-testid="login-link">
                                        <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                                            Login
                                        </Button>
                                    </Link>
                                    <Link to="/register" data-testid="register-link">
                                        <Button size="sm" className="bg-primary hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider">
                                            Join
                                        </Button>
                                    </Link>
                                </div>
                            )}

                            {/* Mobile Menu Toggle */}
                            <button
                                className="md:hidden p-2 text-zinc-400 hover:text-white"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                data-testid="mobile-menu-toggle"
                            >
                                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Nav */}
                    {mobileMenuOpen && (
                        <div className="md:hidden border-t border-white/10 py-3">
                            <nav className="flex flex-col gap-1">
                                {navItems.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`px-4 py-3 text-sm font-medium flex items-center gap-3 ${
                                            isActive(item.path)
                                                ? 'bg-white/10 text-white'
                                                : 'text-zinc-400'
                                        }`}
                                    >
                                        <item.icon className="w-4 h-4" />
                                        {item.label}
                                    </Link>
                                ))}
                                {isAuthenticated && (
                                    <Link
                                        to="/upload"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="px-4 py-3 text-sm font-medium flex items-center gap-3 text-primary"
                                    >
                                        <Upload className="w-4 h-4" />
                                        Upload Performance
                                    </Link>
                                )}
                            </nav>
                        </div>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-16 min-h-screen">
                {children}
            </main>

            {/* Footer */}
            <footer className="border-t border-white/10 py-8 px-4">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                        <span className="text-gold font-mono text-xs tracking-widest">EFFORTLESS METAPHOR</span>
                        <span>presents</span>
                        <span className="font-display font-bold text-white">TRAP<span className="text-neon-blue">WORLD</span></span>
                    </div>
                    <p className="text-xs text-zinc-600 font-mono">
                        SKILL {">"} POPULARITY
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default Layout;
